import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { MerchantContext, normalizeMerchantFromRpc, type Merchant } from './MerchantContextCore';

function normalizeCurrentMerchantPayload(data: unknown): Merchant | null {
    const row = Array.isArray(data) ? data[0] : data;
    if (!row || typeof row !== 'object') return null;
    const merchant = normalizeMerchantFromRpc(row);
    return merchant.establishmentId ? merchant : null;
}

export function MerchantProvider({ children }: { children: ReactNode }) {
    const [merchant, setMerchant] = useState<Merchant | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const refreshMerchant = useCallback(async () => {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) {
            setMerchant(null);
            return null;
        }

        const { data, error } = await supabase.rpc('get_current_merchant');
        if (error) throw error;

        const current = normalizeCurrentMerchantPayload(data);
        setMerchant(current);
        return current;
    }, []);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                await refreshMerchant();
            } catch (err) {
                if (import.meta.env.DEV) console.error('[get_current_merchant]', err);
                if (mounted) setMerchant(null);
            } finally {
                if (mounted) setIsLoading(false);
            }
        };

        void load();

        const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
            if (!mounted) return;
            if (!session) {
                setMerchant(null);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            void (async () => {
                try {
                    const { data, error } = await supabase.rpc('get_current_merchant');
                    if (!mounted) return;
                    if (error) throw error;
                    setMerchant(normalizeCurrentMerchantPayload(data));
                } catch (err) {
                    if (import.meta.env.DEV) console.error('[get_current_merchant]', err);
                    if (mounted) setMerchant(null);
                } finally {
                    if (mounted) setIsLoading(false);
                }
            })();
        });

        return () => {
            mounted = false;
            authListener.subscription.unsubscribe();
        };
    }, [refreshMerchant]);

    const login = useCallback((newMerchant: Merchant) => {
        setMerchant(newMerchant);
        localStorage.removeItem('merchant');
    }, []);

    const logout = useCallback(async () => {
        setMerchant(null);
        localStorage.removeItem('merchant');
        await supabase.auth.signOut();
    }, []);

    return (
        <MerchantContext.Provider
            value={{
                merchant,
                isAuthenticated: !!merchant,
                isLoading,
                login,
                logout,
                refreshMerchant,
            }}
        >
            {children}
        </MerchantContext.Provider>
    );
}
